/**
 * Exercise Category Images
 *
 * Centralized image URLs for exercise categories used across coaching components.
 * These serve as fallback images when exercises don't have custom images.
 *
 * Image sources: Unsplash (https://unsplash.com/license - free to use)
 *
 * To replace with local assets:
 * 1. Download images to /public/images/exercises/
 * 2. Update URLs to use local paths (e.g., '/images/exercises/driving.jpg')
 */

export type ExerciseCategory =
  | 'driving'
  | 'iron_play'
  | 'short_game'
  | 'putting'
  | 'bunker'
  | 'mental'
  | 'fitness'
  | 'warm_up';

/**
 * Category images for exercise cards and detail views
 * 600x300 aspect ratio optimized for card displays
 */
export const EXERCISE_CATEGORY_IMAGES: Record<string, string> = {
  // Legacy DB categories
  driving:    'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=600&h=300&fit=crop&q=80',
  iron_play:  'https://images.unsplash.com/photo-1691608787603-ba908a78e280?w=600&h=300&fit=crop&q=80',
  short_game: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=600&h=300&fit=crop&q=80',
  putting:    'https://images.unsplash.com/photo-1562204320-a243f073adf2?w=600&h=300&fit=crop&crop=left&q=80',
  bunker:     'https://images.unsplash.com/photo-1698426002572-01af3df03c13?w=600&h=300&fit=crop&q=80',
  mental:     'https://images.unsplash.com/photo-1596727362302-b8d891c42ab8?w=600&h=300&fit=crop&q=80',
  fitness:    'https://images.unsplash.com/photo-1611374243147-44a702c2d44c?w=600&h=300&fit=crop&q=80',
  warm_up:    'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=300&fit=crop&q=80',
  // Practice categories
  long_game:  'https://images.unsplash.com/photo-1690288505632-bb7f38d663d4?w=600&h=300&fit=crop&q=80',
  chipping:   'https://images.pexels.com/photos/6542395/pexels-photo-6542395.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop',
  pitching:   'https://images.unsplash.com/photo-1662224107406-cfbd51edd90c?w=600&h=300&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.75&q=80',
};

/**
 * Default fallback image when category is unknown
 */
export const DEFAULT_EXERCISE_IMAGE = EXERCISE_CATEGORY_IMAGES.driving;

/**
 * Get the appropriate image URL for an exercise category
 * Falls back to default image if category is not recognized
 */
export function getExerciseCategoryImage(category: string | undefined | null): string {
  if (!category) return DEFAULT_EXERCISE_IMAGE;

  const normalizedCategory = category.toLowerCase().replace(/\s+/g, '_') as ExerciseCategory;
  return EXERCISE_CATEGORY_IMAGES[normalizedCategory] || DEFAULT_EXERCISE_IMAGE;
}
